import {
  ReScheduleToPublish,
  ReScheduleToUnpublish,
  cancelcrossicon,
} from "@platformx/shared/static-assets";
import { CommonPlateformXDialog, XMenuItem, isScheduledInFuture } from "@platformx/utilities";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { RescheduleDialog } from "./RescheduleDialog";

type Kind = "publish" | "unpublish";
type OpenDialog = { type: "reschedule" | "cancel"; kind: Kind } | null;

type UseScheduleMenuArgs = {
  listItemDetails: any;
  category: string;
  subCategory: string;
  /** Moves a pending schedule. Resolves true when it worked. */
  reschedule?: (item: any, kind: Kind, isoTime: string) => Promise<boolean> | boolean | void;
  /** Removes a pending schedule. */
  cancelSchedule?: (item: any, kind: Kind) => Promise<boolean> | boolean | void;
  /** Closes the kebab menu once an item is chosen. */
  closeMenu?: () => void;
};

/**
 * The amend / cancel half of scheduling, for a content row's kebab menu (KIWI-6576). Both content
 * menus (fixed types and schema-based types) call this, so they cannot drift apart.
 *
 * Returns `items` — the menu entries, to go inside the `XMenu` — and `dialogs`, which must render
 * OUTSIDE it. Items appear only while the corresponding trigger is still in the future, and are
 * driven by the trigger datetimes, never by the item's status: for schema types the status does not
 * change when a schedule fires, so it cannot be trusted here.
 */
export const useScheduleMenu = ({
  listItemDetails,
  category,
  subCategory,
  reschedule,
  cancelSchedule,
  closeMenu,
}: UseScheduleMenuArgs) => {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<OpenDialog>(null);

  const publishAt = listItemDetails?.scheduledPublishTriggerDateTime;
  const unpublishAt = listItemDetails?.scheduledUnPublishTriggerDateTime;
  const isPage = String(listItemDetails?.tagName ?? listItemDetails?.contentType ?? "")
    .toLowerCase()
    .includes("sitepage");
  // Pages have their own schedule menu (PageMenu); this is for content only.
  const enabled = Boolean(reschedule && cancelSchedule) && !isPage;

  const close = () => setDialog(null);

  const onMenuAction = (action: string) => {
    switch (action) {
      case "reschedule_publish":
        setDialog({ type: "reschedule", kind: "publish" });
        break;
      case "reschedule_unpublish":
        setDialog({ type: "reschedule", kind: "unpublish" });
        break;
      case "cancel_publish":
        setDialog({ type: "cancel", kind: "publish" });
        break;
      case "cancel_unpublish":
        setDialog({ type: "cancel", kind: "unpublish" });
        break;
      default:
        break;
    }
    closeMenu?.();
  };

  const item = (menuAction: string, action: string, icon: string) => (
    <XMenuItem
      key={menuAction}
      category={category}
      subCategory={subCategory}
      // Whoever may publish (or unpublish) this type may amend or cancel that schedule.
      action={action}
      menuCallBack={onMenuAction}
      menuAction={menuAction}
      icon={icon}
      label={menuAction}
    />
  );

  const items = [
    ...(enabled && isScheduledInFuture(publishAt)
      ? [
          item("reschedule_publish", "publish", ReScheduleToPublish),
          item("cancel_publish", "publish", cancelcrossicon),
        ]
      : []),
    ...(enabled && isScheduledInFuture(unpublishAt)
      ? [
          item("reschedule_unpublish", "unpublish", ReScheduleToUnpublish),
          item("cancel_unpublish", "unpublish", cancelcrossicon),
        ]
      : []),
  ];

  let dialogs: JSX.Element | null = null;
  if (enabled && dialog?.type === "reschedule") {
    dialogs = (
      <RescheduleDialog
        open
        kind={dialog.kind}
        initialTime={dialog.kind === "publish" ? publishAt : unpublishAt}
        onClose={close}
        onConfirm={async (isoTime) => {
          // The dialog closes whatever the outcome: a refusal is already reported by a toast and
          // leaves the row untouched, so keeping the dialog open would only invite a retry.
          await reschedule?.(listItemDetails, dialog.kind, isoTime);
          close();
        }}
      />
    );
  } else if (enabled && dialog?.type === "cancel") {
    dialogs = (
      <CommonPlateformXDialog
        isDialogOpen
        title={t(dialog.kind === "publish" ? "cancel_publish" : "cancel_unpublish")}
        subTitle={t("cancel_schedule_content_message")}
        closeButtonText={t("no")}
        closeButtonHandle={close}
        confirmButtonText={t("yes")}
        confirmButtonHandle={async () => {
          await cancelSchedule?.(listItemDetails, dialog.kind);
          close();
        }}
        modalType='delete'
      />
    );
  }

  return { items, dialogs };
};
